import React from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { ArrowLeft, Truck, Clock, MapPin, Phone, CheckCircle } from 'lucide-react';
import DownloadApp from '@/components/shared/DownloadApp';
import { getFullUrl } from "@/lib/utils";

const Processing = () => {
  const processSteps = [
    {
      step: "Document Verification",
      status: "completed",
      time: "30 minutes",
      description: "We verify all documents are complete and correct"
    },
    {
      step: "Queue Management",
      status: "in-progress",
      time: "2-4 hours",
      description: "Our team joins the queue at the licensing department"
    },
    {
      step: "Application Processing",
      status: "pending",
      time: "1-2 hours",
      description: "Processing your application with the authorities"
    },
    {
      step: "Quality Check",
      status: "pending",
      time: "15 minutes",
      description: "Final verification of completed documents"
    }
  ];

  const trackingFeatures = [
    {
      title: "Real-time Updates",
      description: "In mobile app notifications on your Android, iPhone or Huawei device",
      icon: <Phone className="w-6 h-6 text-blue-600" />
    },
    {
      title: "Location Tracking",
      description: "Know which licensing office we're at",
      icon: <MapPin className="w-6 h-6 text-blue-600" />
    },
    {
      title: "Time Estimates",
      description: "Accurate completion time predictions",
      icon: <Clock className="w-6 h-6 text-blue-600" />
    }
  ];

  return (
    <>
      <Head>
        <title>Processing & Tracking - We Queue For You | EzyFind Car Spares</title>
        <meta name="description" content="Track your vehicle licensing application progress in real-time. Professional queue management services." />
      </Head>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-blue-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link href={getFullUrl("we-queue-for-you")} className="inline-flex items-center gap-2 text-blue-200 hover:text-white mb-4">
              <ArrowLeft className="w-5 h-5" />
              Back to We Queue For You
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold">Processing & Tracking</h1>
            <p className="text-xl text-blue-100 mt-2">Monitor your application progress in real-time</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Progress Tracking */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <Truck className="w-8 h-8 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">Current Progress</h2>
              </div>
              
              <div className="space-y-6">
                {processSteps.map((step, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      step.status === 'completed' 
                        ? 'bg-green-100 text-green-600'
                        : step.status === 'in-progress'
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      {step.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <span className="text-sm font-bold">{index + 1}</span>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{step.step}</h3>
                      <p className="text-sm text-gray-600 mb-1">{step.description}</p>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500">Est. {step.time}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          step.status === 'completed' 
                            ? 'bg-green-100 text-green-800'
                            : step.status === 'in-progress'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {step.status.replace('-', ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Next Update Expected</h4>
                <p className="text-blue-700 text-sm">You'll receive website or mobile app notifications at every step of the process.</p>
              </div>
            </div>

            {/* Tracking Features */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Tracking Features</h2>
                
                <div className="space-y-6">
                  {trackingFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="flex-shrink-0">{feature.icon}</div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                        <p className="text-gray-600 text-sm">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Need Help?</h2>
                <p className="text-gray-600 mb-4">Contact our support team for real-time assistance.</p>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-blue-600" />
                    <span>087 265 7574</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    <span>Currently at: Randburg Licensing Dept</span>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
      <DownloadApp/>
    </>
  );
};

export default Processing;